/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all connectors (connector ratings) from the API
 */
export const fetchAllConnectors = createAsyncThunk(
  'connector/fetchAllConnectors',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.connecterRateList(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch connectors',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Refresh connectors without toggling loading state
 */
export const refreshConnectors = createAsyncThunk(
  'connector/refreshConnectors',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.connecterRateList(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh connectors',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Edit an existing connector rating via PATCH
 */
export const editConnector = createAsyncThunk(
  'connector/editConnector',
  async (
    { id, data }: { id: string; data: Record<string, any> },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await deviceHubApi.updateConnectorRating(id, data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to update connector rating',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);


/**
 * Fetch connector filter options
 */
export const fetchConnectorPageFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'connector/fetchConnectorPageFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.connectorRatingFilter(query);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response?.data);
      console.log('updatedResponse', updatedResponse, response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Connector slice
 */
const initialState = {
  connectors: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 20,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

const connectorSlice = createSlice({
  name: 'connector',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setConnectorPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setConnectorLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateConnectorPageFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableConnectorPageFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setConnectorSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setConnectorSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllConnectors.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllConnectors.fulfilled, (state, action) => {
        state.loading = false;
        state.connectors = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllConnectors.rejected, (state) => {
        state.loading = false;
      })
      .addCase(refreshConnectors.fulfilled, (state, action) => {
        // Update without toggling loading to avoid UI flicker
        state.connectors = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchConnectorPageFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConnectorPageFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConnectorPageFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setConnectorPage,
  setConnectorLimit,
  updateConnectorPageFilters,
  disableConnectorPageFilterApplied,
  setConnectorSortBy,
  setConnectorSortField,
} = connectorSlice.actions;

export default connectorSlice.reducer;
